// 📄 components/ui/OneHubLogo.tsx
// Isotipo de OneHub: un nodo central geométrico con órbitas/conexiones, en
// la paleta cian (#00D2FF) → morado (#8A2BE2) del tema oscuro de la
// plataforma. SVG puro (sin dependencias de íconos externos) para poder
// reescalarlo libremente: navbar, pantalla de login, favicon, etc.
import { useId } from 'react';

interface OneHubLogoProps {
  // Ancho y alto en píxeles (el SVG es cuadrado) — por defecto 40.
  size?: number;
  className?: string;
  // Texto accesible (aria-label) para lectores de pantalla.
  title?: string;
}

export default function OneHubLogo({ size = 40, className, title = 'OneHub' }: OneHubLogoProps) {
  // useId evita colisiones de <linearGradient>/<filter> cuando el logo se
  // renderiza más de una vez en la misma página (ej. navbar + login).
  const uid = useId();
  const cyanId = `${uid}-cyan`;
  const purpleId = `${uid}-purple`;
  const nodeId = `${uid}-node`;
  const glowId = `${uid}-glow`;

  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title}
    >
      <defs>
        <linearGradient id={cyanId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00D2FF" />
          <stop offset="100%" stopColor="#5CE6FF" />
        </linearGradient>
        <linearGradient id={purpleId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8A2BE2" />
          <stop offset="100%" stopColor="#B266FF" />
        </linearGradient>
        <linearGradient id={nodeId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00D2FF" />
          <stop offset="100%" stopColor="#8A2BE2" />
        </linearGradient>
        <filter id={glowId} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="2.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Órbita cian: elipse + dos nodos satélite conectados al centro */}
      <g transform="rotate(28 24 24)" opacity={0.85}>
        <ellipse cx="24" cy="24" rx="19" ry="7" fill="none" stroke={`url(#${cyanId})`} strokeWidth="1.4" opacity={0.55} />
        <line x1="24" y1="24" x2="43" y2="24" stroke={`url(#${cyanId})`} strokeWidth="1.2" opacity={0.45} strokeLinecap="round" />
        <line x1="24" y1="24" x2="5" y2="24" stroke={`url(#${cyanId})`} strokeWidth="1.2" opacity={0.3} strokeLinecap="round" />
        <circle cx="43" cy="24" r="2.6" fill={`url(#${cyanId})`} />
        <circle cx="5" cy="24" r="1.6" fill={`url(#${cyanId})`} opacity={0.7} />
      </g>

      {/* Órbita morada, espejada respecto a la cian */}
      <g transform="rotate(-28 24 24)" opacity={0.85}>
        <ellipse cx="24" cy="24" rx="19" ry="7" fill="none" stroke={`url(#${purpleId})`} strokeWidth="1.4" opacity={0.55} />
        <line x1="24" y1="24" x2="43" y2="24" stroke={`url(#${purpleId})`} strokeWidth="1.2" opacity={0.3} strokeLinecap="round" />
        <line x1="24" y1="24" x2="5" y2="24" stroke={`url(#${purpleId})`} strokeWidth="1.2" opacity={0.45} strokeLinecap="round" />
        <circle cx="43" cy="24" r="1.6" fill={`url(#${purpleId})`} opacity={0.7} />
        <circle cx="5" cy="24" r="2.6" fill={`url(#${purpleId})`} />
      </g>

      {/* Nodo central geométrico */}
      <polygon
        points="30,24 27,29.2 21,29.2 18,24 21,18.8 27,18.8"
        fill={`url(#${nodeId})`}
        filter={`url(#${glowId})`}
      />
      <polygon
        points="30,24 27,29.2 21,29.2 18,24 21,18.8 27,18.8"
        fill="none"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="0.6"
      />
    </svg>
  );
}
